import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import clsx from 'clsx';
import flatpickr from 'flatpickr';
import {
  convertPathToDotNotation,
  getDateFormat,
  getEnableNativeMobileFunction,
  getPageLocale,
  handleKeyDown,
  isMyMscArea,
  isUS,
  langLocaleMap,
  replacePlaceholders,
  RootState,
  yearDropdownPlugin,
} from '@msc-b2c/shared-utils';
import { Icon } from '../../icons/Icon';
import { useSelector } from 'react-redux';
import { GuestType } from '@msc-b2c/interfaces/types';
import styles from './datepicker.module.scss';

type DatePickerInputProps = {
  label: string;
  name: string;
  index: number;
  control: Control<any>;
  errors: FieldErrors;
  clearErrors: UseFormClearErrors<any>;
  setError: UseFormSetError<any>;
  setValue: UseFormSetValue<any>;
  disabled?: boolean;
  isLowerLabel?: boolean;
  validation?: any;
  inputTypeDate?: string;
  disableCheckAgePopup?: boolean;
  styleOptions?: string;
  guestType?: GuestType;
  minAge: number | undefined;
  invalidDateErrorMessage?: string;
  dateValues?: string;
  calendarIconAccessibilityLabel?: string;
};

export const DatePickerInput = ({
  label,
  name,
  index,
  control,
  errors,
  clearErrors,
  setError,
  setValue,
  disabled,
  isLowerLabel,
  validation,
  guestType,
  minAge,
  invalidDateErrorMessage,
  calendarIconAccessibilityLabel,
  inputTypeDate,
  disableCheckAgePopup,
  styleOptions = '',
  dateValues,
}: DatePickerInputProps) => {
  const { areInfantsAllowed, ageRestriction } = useSelector((state: RootState) => state.cruiseData);
  const noInfantsAllowed = areInfantsAllowed !== undefined && !areInfantsAllowed;

  const dateInputRef = useRef(null);

  const isError = convertPathToDotNotation(errors, name);
  const isErrorClass = isError ? 'error-input' : '';
  const isDisabled = disabled ? 'disabled-input' : '';
  const typeDate = inputTypeDate ? inputTypeDate : label;
  const labelClass = isLowerLabel
    ? 'cmp-input__lowerlabel'
    : (noInfantsAllowed && guestType === GuestType.MINOR) ||
      (ageRestriction.minimumAge !== undefined &&
        ageRestriction?.minimumAge !== 18 &&
        guestType === GuestType.ADULT)
    ? 'cmp-input__ageRestriction'
    : 'cmp-input__upperlabel';

  const checkErrorMessageTooLong = (errorMessage: Element) => {
    const lineHeight = parseFloat(getComputedStyle(errorMessage).lineHeight);
    const height = errorMessage.clientHeight;
    if (height > lineHeight) {
      return true;
    }
    return false;
  };

  // check if we have an error message whick is too long
  useEffect(() => {
    if (dateInputRef.current) {
      const errorElement = (dateInputRef.current as HTMLElement).querySelector(
        '.error-list-validation > div'
      );
      const isLongErrorText = errorElement ? checkErrorMessageTooLong(errorElement) : false;

      // if we have the error and error text is too long add a margin
      if (isLongErrorText) {
        (dateInputRef.current as HTMLElement).classList.add(styles['cmp-form-text__long-error']);
      } else {
        (dateInputRef.current as HTMLElement).classList.remove(styles['cmp-form-text__long-error']);
      }
    }
  }, [isError]);

  return (
    <div className={`text ${labelClass} ${isErrorClass} ${isDisabled} ${styleOptions}`}>
      <div
        className="cmp-form-text"
        ref={dateInputRef}
      >
        {!isLowerLabel && (
          <label
            className={clsx('cmp-label', isErrorClass)}
            htmlFor={`${name}-${index}`}
          >
            {label}
          </label>
        )}
        <Controller
          name={name}
          control={control}
          rules={validation}
          render={({ field }) => (
            <FlatpickrInputWrapper
              dateValues={dateValues}
              field={field}
              name={name}
              index={index}
              clearErrors={clearErrors}
              setError={setError}
              setValue={setValue}
              isError={!!isError}
              disabled={disabled}
              label={typeDate}
              errors={errors}
              disableCheckAgePopup={disableCheckAgePopup}
              minAge={minAge}
              invalidDateErrorMessage={invalidDateErrorMessage}
              calendarIconAccessibilityLabel={calendarIconAccessibilityLabel}
            />
          )}
        />
        {isError && (
          <div className="error-list-validation">
            <div>{isError.message?.toString()}</div>
          </div>
        )}
      </div>
    </div>
  );
};

// To support all AEM formats dynamically (/, -, day, month, year order), dateFormat should be
// aligned in context-aware.

// AEM getDateFormat() is not applicable now
// AEM dateFormat today is not consistent. (e.g. EU countries use in AEM yyyy-mm-dd => display DD/MM/YYYY).

const DAYJS_TO_FLATPICKR: Record<string, string> = {
  'YYYY-MM-DD': 'Y-m-d',
  'DD/MM/YYYY': 'd/m/Y',
  'MM/DD/YYYY': 'm/d/Y',
  'YYYY/MM/DD': 'Y/m/d',
  'MM-DD-YYYY': 'm/d/Y',
  'DD-MM-YYYY': 'd/m/Y',
};

//  TODO MANAGE CORRECTLY FOR ALL THE COUNTRIES
//  Current behaviour:
//  US => MM/DD/YYYY (month-first, slash mask)
//  Non-US => DD/MM/YYYY (day-first, slash mask)
// Form/submit value is always iso YYYY-MM-DD.

const resolveDisplayFormat = (): string => {
  if (isUS()) {
    return 'MM/DD/YYYY';
  }
  return 'DD/MM/YYYY';
};

type FlatpickrInputWrapperProps = {
  field: any;
  name: string;
  index: number;
  clearErrors: UseFormClearErrors<any>;
  setError: UseFormSetError<any>;
  setValue: UseFormSetValue<any>;
  isError: boolean;
  disabled?: boolean;
  label: string;
  errors: FieldErrors;
  disableCheckAgePopup?: boolean;
  minAge?: number;
  invalidDateErrorMessage?: string;
  dateValues?: string;
  calendarIconAccessibilityLabel?: string;
};

const FlatpickrInputWrapper = ({
  field,
  name,
  index,
  clearErrors,
  setError,
  setValue,
  isError,
  disabled,
  label,
  errors,
  disableCheckAgePopup,
  minAge,
  invalidDateErrorMessage,
  dateValues,
  calendarIconAccessibilityLabel,
}: FlatpickrInputWrapperProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const flatpickrInstance = useRef<any>(null); // Reference to Flatpickr instance
  const userInputValueRef = useRef<string>(''); // Track actual user input before flatpickr modifies it
  const { adultsOccupancy, childrenOccupancy, infantOccupancy, date } = useSelector(
    (state: RootState) => state.bookingParams
  );

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const minDateRef = useRef<Date | null>(null);
  const maxDateRef = useRef<Date | null>(null);
  const minDateIsoRef = useRef<string | null>(null);
  const maxDateIsoRef = useRef<string | null>(null);

  // One format for input text + calendar + blur parsing, so month first markets cannot swap month/day on reopen.
  const displayFormat = resolveDisplayFormat();
  const isMonthFirst = displayFormat === 'MM/DD/YYYY';
  const resolvedFlatFormat = DAYJS_TO_FLATPICKR[displayFormat];

  // Convert typed text -> "YYYY-MM-DD" using string parsing only (no Date/timezone/dayjs).
  const displayToIsoDate = (value?: string | null): string | null => {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((value ?? '').trim());
    if (!match) return null;
    const [, first, second, yyyy] = match;
    const [mm, dd] = isMonthFirst ? [first, second] : [second, first];
    const month = Number(mm);
    const day = Number(dd);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${yyyy}-${mm}-${dd}`;
  };

  // Format date for display (YYYY-MM-DD to the format this market types)
  const formatDateForDisplay = (value: string | null) => {
    if (!value) return '';
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value;

    let out = '';
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (isoMatch) {
      const [, yyyy, mm, dd] = isoMatch;
      out = isMonthFirst ? `${mm}/${dd}/${yyyy}` : `${dd}/${mm}/${yyyy}`;
    } else {
      const date = dayjs(value);
      out = date.isValid() ? date.format(displayFormat) : '';
    }
    return out;
  };

  // Detect touch-first devices (mobile).
  // Note: Chrome DevTools mobile emulation forces these to true by design.
  const isMobileDevice =
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(pointer: coarse)').matches ||
      window.matchMedia?.('(any-pointer: coarse)').matches ||
      navigator.maxTouchPoints > 0);

  // Apply mobile DOB keyboard/calendar separation only when
  // native mobile behavior is enabled via feature flag (US market feature)
  const enableNativeMobile = getEnableNativeMobileFunction();
  const shouldApplyMobileDOBLogic = isMobileDevice && enableNativeMobile;

  useEffect(() => {
    // Set the input value to display format when field value changes
    if (inputRef.current && field.value) {
      // Convert the stored YYYY-MM-DD into the text this market reads
      inputRef.current.value = formatDateForDisplay(field.value) || field.value;
    } else if (inputRef.current && !field.value) {
      inputRef.current.value = '';
    }

    // Only blur if THIS input is currently focused.
    // Dont steal focus back from the next field
    setTimeout(() => {
      if (!inputRef.current) return;
      if (!inputRef.current.value) return;

      if (document.activeElement === inputRef.current) {
        inputRef.current.blur();
      }
    });
  }, [field.value]);

  useEffect(() => {
    if (inputRef.current) {
      const today = dayjs().utc(true);
      let sailingDate = today;
      if (date) {
        const sailingDateString = date.split('-')[0];
        if (sailingDateString) sailingDate = dayjs.utc(sailingDateString, 'DD/MM/YYYY');
      }
      const eighteenYearsAgo = dayjs(sailingDate).subtract(minAge ?? 18, 'year');
      const twoYearsBeforeSailingPlusOneDay = dayjs(sailingDate).subtract(2, 'year').add(1, 'day');
      const twoYearsBeforeSailing = dayjs(sailingDate).subtract(2, 'year');
      const adultsCount = adultsOccupancy ?? 0; // Default to 0 if undefined
      const childrenCount = childrenOccupancy ?? 0; // Default to 0 if undefined
      const infantCount = infantOccupancy ?? 0;

      // Determine maxDate based on age group
      let selectedYear = today;
      let maxDate = today;
      let minDate = today.add(50, 'year');
      let paxType;

      if (isMyMscArea()) {
        //This scenario is for use the date picker outside of booking funnel
        paxType = '';
      } else {
        //This scenario is for use the date picker inside the booking funnel
        // Determine the paxType based on the index
        if (index < adultsCount) {
          paxType = 'Adult';
        } else if (adultsCount <= index && index < adultsCount + childrenCount) {
          paxType = 'Minor';
        } else {
          paxType = 'Infant';
        }
      }

      if (name.includes('dateOfBirth')) {
        // Apply date restrictions based on paxType
        if (paxType === 'Adult') {
          minDate = dayjs(maxDate).subtract(100, 'year');
          maxDate = eighteenYearsAgo;
        } else if (paxType === 'Minor') {
          minDate = dayjs(sailingDate).subtract(18, 'year').add(1, 'day');
          maxDate = twoYearsBeforeSailing;
        } else if (paxType === 'Infant') {
          minDate = twoYearsBeforeSailingPlusOneDay;
          maxDate = today;
        } else {
          minDate = dayjs(today).subtract(100, 'year');
          maxDate = dayjs(today).subtract(minAge ?? 18, 'year');
        }
        selectedYear = maxDate;
      }

      if (
        (name.includes('guest.dateOfBirthAsString') && adultsCount <= 0) ||
        name.includes('dateOfBirthAsString')
      ) {
        minDate = dayjs(today).subtract(100, 'year');
        maxDate = dayjs(today);
      }

      if (name.includes('signInDetails')) {
        minDate = dayjs(today);
        maxDate = dayjs(today);
        selectedYear = today;
      }

      if (name.includes('pregnancy')) {
        minDate = dayjs(today);
        maxDate = dayjs(today).add(9, 'month');
      }

      if (name.includes('dateOfExpire')) {
        maxDate = dayjs(today).add(10, 'year');
        minDate = dayjs(today).add(1, 'day');
        selectedYear = today;
      }

      if (name.includes('passportDateOfIssue')) {
        maxDate = dayjs(today);
        minDate = dayjs(today).subtract(10, 'year');
        selectedYear = today;
      }

      if (name === 'customer.dateOfBirth') {
        if (disableCheckAgePopup) {
          maxDate = dayjs(today).subtract(18, 'year');
        } else {
          maxDate = dayjs(today);
        }

        minDate = dayjs(maxDate).subtract(100, 'year');
      }

      minDateRef.current = minDate.toDate();
      maxDateRef.current = maxDate.toDate();
      minDateIsoRef.current = minDate.format('YYYY-MM-DD');
      maxDateIsoRef.current = maxDate.format('YYYY-MM-DD');

      const dayjsFormat = displayFormat;

      const instance = flatpickr(inputRef.current, {
        plugins: [
          yearDropdownPlugin({
            selectedYear: selectedYear.format('DD/MM/YYYY'),
            yearStart: minDate.year(),
            yearEnd: maxDate.year(),
          }),
        ],
        dateFormat: resolvedFlatFormat,
        altInput: isMobileDevice ? !shouldApplyMobileDOBLogic : false,
        altFormat: resolvedFlatFormat,
        allowInput: true,
        clickOpens: !shouldApplyMobileDOBLogic,
        disableMobile: true,
        closeOnSelect: true,
        shorthandCurrentMonth: true,
        monthSelectorType: 'dropdown',
        allowInvalidPreload: true,
        locale: langLocaleMap[getPageLocale()],
        maxDate: maxDate.format(dayjsFormat),
        minDate: minDate.format(dayjsFormat),
        onChange: (selectedDates) => {
          const selectedDate = selectedDates[0];

          // Only accept the date if user input was complete (10 chars) or if it came from calendar selection
          // Check userInputValueRef which has the value BEFORE flatpickr modified it
          if (userInputValueRef.current && userInputValueRef.current.length < 10) {
            // Restore the original incomplete input value to the DOM element
            if (inputRef.current) {
              inputRef.current.value = userInputValueRef.current;
            }
            return; // Let handleBlur handle validation
          }

          if (selectedDate) {
            const storedFormat = dayjs(selectedDate).format('YYYY-MM-DD'); // Store in YYYY-MM-DD

            const displayValue = dayjs(selectedDate).format(dayjsFormat);

            // Store in YYYY-MM-DD format for validation
            field.onChange(storedFormat);

            // Update display to the format this market types
            if (inputRef.current) {
              inputRef.current.value = displayValue;
            }

            validateDate(selectedDate);
          }
          setIsCalendarOpen(false);

          // Don't auto-fill with maxDate when calendar is opened without selection
          // Let the user input or select a date explicitly
        },
        onClose: () => {
          // Only handle blur and error, no calendar sync here
          setIsCalendarOpen(false);
        },
        onReady: (_selectedDates, _dateStr, instance) => {
          //force numeric keyboard
          if (shouldApplyMobileDOBLogic && instance.altInput) {
            instance.altInput.setAttribute('inputmode', 'numeric');
            instance.altInput.setAttribute('pattern', '[0-9/]*');
          }
          instance.calendarContainer.classList.add(`${name.split('.')[1]}-${index}`);
          const monthDropdown = instance.monthsDropdownContainer;
          if (monthDropdown) {
            monthDropdown.addEventListener('click', () => {
              monthDropdown.childNodes.forEach((option, index) => {
                if (option instanceof HTMLElement) {
                  option.textContent =
                    instance.l10n.months.shorthand[
                      parseInt(option.getAttribute('value') as string) ?? index
                    ];
                }
              });
            });
            monthDropdown.addEventListener('mousedown', () => {
              monthDropdown.childNodes.forEach((option, index) => {
                if (option instanceof HTMLElement) {
                  option.textContent =
                    instance.l10n.months.shorthand[
                      parseInt(option.getAttribute('value') as string) ?? index
                    ];
                }
              });
            });

            monthDropdown.addEventListener('mouseout', () => {
              monthDropdown.childNodes.forEach((option, index) => {
                if (option instanceof HTMLElement) {
                  option.textContent =
                    instance.l10n.months.shorthand[
                      parseInt(option.getAttribute('value') as string) ?? index
                    ];
                }
              });
            });
            monthDropdown.addEventListener('change', () => {
              monthDropdown.childNodes.forEach((option, index) => {
                if (option instanceof HTMLElement) {
                  option.textContent =
                    instance.l10n.months.shorthand[
                      parseInt(option.getAttribute('value') as string) ?? index
                    ];
                }
              });
            });
          }
          if (shouldApplyMobileDOBLogic && instance.altInput) {
            instance.altInput.addEventListener('focus', () => {
              if (instance.isOpen) {
                instance.close();
                instance.altInput?.focus();
              }
            });
          }
          setIsCalendarOpen(true);
        },
        onOpen: (selectedDates, _dateStr, instance) => {
          // Match calendar width to input field width
          const inputWrapper = inputRef.current?.closest(
            '.date-picker-input-wrapper'
          ) as HTMLElement;
          if (inputWrapper) {
            instance.calendarContainer.style.width = `${inputWrapper.offsetWidth}px`;
          }

          // Clear tracked input when calendar opens (user is selecting from calendar)
          userInputValueRef.current = '';

          let dateToShow = selectedDates[0] || null;
          if (!dateToShow) return;
          if (inputRef.current?.value) {
            const parsedDate = dayjs(inputRef.current.value, dayjsFormat, true);
            if (
              parsedDate.isValid() &&
              minDateRef.current &&
              maxDateRef.current &&
              parsedDate.isAfter(dayjs(minDateRef.current).subtract(1, 'day')) &&
              parsedDate.isBefore(dayjs(maxDateRef.current).add(1, 'day'))
            ) {
              dateToShow = parsedDate.toDate();
            } else {
              // if date is not in range max and mindate then do not assing it the max date
              dateToShow = maxDateRef.current!;
              userInputValueRef.current = _dateStr;
            }
          }
          if (flatpickrInstance.current && dateToShow) {
            flatpickrInstance.current.jumpToDate(dateToShow);
          }
          if (selectedDates[0] && !isCalendarOpen) {
            instance.close();
            setIsCalendarOpen(true);
          }
        },
      });

      flatpickrInstance.current = instance; // Store the instance
      if (inputRef.current.value !== userInputValueRef.current && userInputValueRef.current) {
        inputRef.current.value = userInputValueRef.current; // Initialize display value
      }

      const handleScroll = () => {
        const calendar = document.querySelector('.flatpickr-calendar') as HTMLElement;
        if (
          calendar &&
          calendar.style.display !== 'none' &&
          calendar.getAttribute('aria-hidden') !== 'true' &&
          flatpickrInstance.current.isOpen
        ) {
          instance.close();
        }
      };

      document.body.addEventListener('scroll', handleScroll);
      return () => {
        document.body.removeEventListener('scroll', handleScroll);
      };
    }
  }, [name, isCalendarOpen, displayFormat]);

  const handleBlur = () => {
    // Use tracked value if available (before flatpickr modified it), otherwise use current input
    let inputValue = userInputValueRef.current || inputRef.current?.value?.trim() || '';

    // If flatpickr cleared or modified the input, restore the tracked value
    if (
      userInputValueRef.current &&
      inputRef.current &&
      inputRef.current.value !== userInputValueRef.current
    ) {
      inputRef.current.value = userInputValueRef.current;
      inputValue = userInputValueRef.current;
    }

    // Clear the tracked user input on blur
    userInputValueRef.current = '';

    if (!inputValue) {
      setValue(name, inputValue, { shouldValidate: false });
      field.onBlur();
      return;
    }

    const invalidDateError = {
      type: 'manual',
      message: replacePlaceholders(invalidDateErrorMessage ?? 'Invalid date', {
        fieldName: label,
      }),
    };

    // Incomplete / unparseable text => generic invalid-date message
    const rejectTypedValue = (value: string) => {
      setValue(name, value, { shouldValidate: false });
      setError(name, invalidDateError);
    };

    // Real date outside the field range, store iso and let the form's
    // shared validation rules produce the authored
    // message
    const commitOutOfRangeDate = (iso: string) => {
      field.onChange(iso);
      setValue(name, iso, { shouldValidate: true });
      if (inputRef.current) {
        inputRef.current.value = dayjs(iso).format(displayFormat);
      }
    };

    // Incomplete typed value (not a full DD/MM/YYYY or MM/DD/YYYY)
    if (inputValue.length < 10) {
      rejectTypedValue(inputValue);
      // Don't call field.onBlur() — it can clear the manual error via validation.
      return;
    }

    const parsedDate = dayjs(inputValue, displayFormat, true);
    // Parse + compare entirely as YYYY-MM-DD strings (no Date/dayjs/timezone), so it is
    // stable regardless of the browser timezone or environment.
    const isoDate = displayToIsoDate(inputValue);
    const minIso = minDateIsoRef.current;
    const maxIso = maxDateIsoRef.current;
    // Special handling for signInDetails - must be exactly today (no auto-correction; show error so user enters it manually)
    if (name.includes('signInDetails')) {
      const todayIso = dayjs().format('YYYY-MM-DD');

      if (!isoDate || isoDate !== todayIso) {
        field.onChange('');
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        setError(name, {
          type: 'manual',
          message: replacePlaceholders(invalidDateErrorMessage ?? '', {
            fieldName: label,
          }),
        });
        field.onBlur();
        return;
      }
    }

    if (!parsedDate.isValid() || !isoDate) {
      rejectTypedValue(inputValue);
      return;
    }

    const outOfRange = (!!minIso && isoDate < minIso) || (!!maxIso && isoDate > maxIso);

    if (outOfRange) {
      commitOutOfRangeDate(isoDate);
      field.onBlur();
      return;
    }

    // Valid + in range — store ISO, show market display format, sync calendar
    clearErrors(name);
    field.onChange(isoDate);
    setValue(name, isoDate, { shouldValidate: false });
    if (inputRef.current) {
      inputRef.current.value = parsedDate.format(displayFormat);
    }
    if (flatpickrInstance.current && !flatpickrInstance.current.isOpen) {
      flatpickrInstance.current.setDate(inputValue, false);
      flatpickrInstance.current.jumpToDate(inputValue);
    }
    field.onBlur();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> & { nativeEvent: InputEvent }) => {
    const target = e.target as HTMLInputElement;
    const rawDigits = target.value.replace(/\D/g, ''); // Rimuove tutto tranne i numeri
    const isBackspace = e.nativeEvent.inputType === 'deleteContentBackward';
    let cursorPosition = target.selectionStart || 0; // Posizione attuale del cursore
    let formattedValue = '';

    for (let i = 0; i < rawDigits.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formattedValue += '/';
        if (cursorPosition === i + 1 && !isBackspace) {
          cursorPosition++;
        }
      }
      formattedValue += rawDigits[i];
    }

    // Track the user's actual input before flatpickr can modify it
    userInputValueRef.current = formattedValue;

    target.value = formattedValue;

    if (isBackspace) {
      if (cursorPosition === 3 || cursorPosition === 6) {
        cursorPosition--;
      } else if (cursorPosition === 5) {
        cursorPosition = 4;
      }
    } else if (cursorPosition === 3 || cursorPosition === 6) {
      cursorPosition++;
    }
    target.setSelectionRange(cursorPosition, cursorPosition);
  };

  const validateDate = (date: Date | null) => {
    const requiredErrorMessage = invalidDateErrorMessage ?? 'Invalid date';
    if (!date || isNaN(date.getTime())) {
      setError(name, {
        type: 'required',
        message: replacePlaceholders(requiredErrorMessage, { fieldName: label }),
      });
      return;
    }
    clearErrors(name);
  };

  const ignoreNextClickRef = { current: false };

  const openCalendarFromIcon = (
    e:
      | React.PointerEvent<HTMLSpanElement>
      | React.MouseEvent<HTMLSpanElement>
      | React.TouchEvent<HTMLSpanElement>
      | React.KeyboardEvent<HTMLSpanElement>
  ) => {
    e.preventDefault();
    e.stopPropagation();

    // Clear tracked input so calendar selection works
    userInputValueRef.current = '';

    // Prevent keyboard overlap on touch devices (esp. Android)
    if (shouldApplyMobileDOBLogic) {
      inputRef.current?.blur();
    }

    flatpickrInstance.current?.open?.();
  };

  const handleIconPointerDown = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (!shouldApplyMobileDOBLogic) return; // keep desktop behavior unchanged
    ignoreNextClickRef.current = true;
    openCalendarFromIcon(e);
  };

  const handleIconMouseDown = (e: React.MouseEvent<HTMLSpanElement>) => {
    if (!shouldApplyMobileDOBLogic) return;
    ignoreNextClickRef.current = true;
    openCalendarFromIcon(e);
  };

  const handleIconTouchStart = (e: React.TouchEvent<HTMLSpanElement>) => {
    if (!shouldApplyMobileDOBLogic) return;
    ignoreNextClickRef.current = false;
    openCalendarFromIcon(e);
  };

  // Open calendar on icon click
  const handleIconClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    // If we already handled pointer/touch, ignore the trailing click
    if (ignoreNextClickRef.current) {
      ignoreNextClickRef.current = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    openCalendarFromIcon(e);
  };

  const handleIconKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    openCalendarFromIcon(e);
  };

  // Close calendar if browser's back button is pushed
  window.addEventListener('popstate', () => flatpickrInstance.current?.close());

  return (
    <div className="date-picker-input-wrapper">
      <input
        data-date-values={dateValues ?? ''}
        ref={inputRef}
        name={name}
        autoComplete="off"
        id={`${name}-${index}`}
        type="text"
        pattern={shouldApplyMobileDOBLogic ? '[0-9/]*' : undefined}
        inputMode="numeric" // Show numeric keyboard on mobile devices
        className={clsx('cmp-form-text__text form-control input active', {
          'error-input': convertPathToDotNotation(errors, name),
        })}
        placeholder={label}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={() => {
          clearErrors(name);
          // Track current value when user focuses
          userInputValueRef.current = inputRef.current?.value || '';
          if (shouldApplyMobileDOBLogic && flatpickrInstance.current?.isOpen) {
            setTimeout(() => {
              flatpickrInstance.current.close();
            }, 0);
          }
        }}
        aria-invalid={isError ? 'true' : 'false'}
        defaultValue={formatDateForDisplay(field.value)}
      />
      <span
        className="calendar-icon"
        role="button"
        tabIndex={0}
        aria-label={calendarIconAccessibilityLabel}
        onPointerDown={handleIconPointerDown}
        onTouchStart={handleIconTouchStart} // iOS fallback
        onMouseDown={handleIconMouseDown} // older browsers fallback
        onClick={handleIconClick} // desktop / accessibility fallback
        onKeyDown={handleIconKeyDown}
        style={{
          position: 'absolute',
          top: '50%', // Center vertically
          right: '2px',
          backgroundColor: 'white',
          transform: 'translateY(-50%)', // Correct for the vertical alignment
          cursor: 'pointer', // Change cursor to pointer on hover
          fontSize: '1.5em', // Size of the icon; adjust as needed
          pointerEvents: 'all', // Ensure it captures click events
          zIndex: 1, // Bring the icon in front of other elements if necessary
          color: '#555', // Change the icon color; adjust as needed
        }}
      >
        <Icon icon={'calendar'}></Icon>
      </span>
    </div>
  );
};
